import { PriceType } from './api'
import type { List } from './list'
import type { PromptImage, TutorialStep } from './prompt'
import type { RecentItemActionEnum, User } from './user'

export interface ElementalView {
  elementaltype: ElementalTypeEnum
  name: string
  label: string
  url: string
}

export interface ElementalTypeMetadata {
  isActive: boolean
  isSearchable: boolean
  layout: Record<string, string | boolean | number>
  functionalities: Record<string, string | boolean | number>
  validation: Record<string, string | boolean | number | number[] | string[]>
}

export interface ElementalType {
  id: ElementalTypeEnum
  name: string
  url: string
  metadata: ElementalTypeMetadata
}

export function getElementalEndpoint(routePath: string) {
  const path = getRouteFromElemental(routePath)
  const elementals = useElementalTypes().value.find(e => e.name === path)
  return elementals?.url || routePath
}

export function getRouteFromElemental(routePath: string) {
  return (
    routePath.match(
      /^\/(prompt|list|snippet|automation|document|output|table).*/,
    )?.[1] || 'prompt'
  )
}

export function getElementalSection(
  elementalType?: ElementalType | null,
  section?: string,
  locationMatcher?: 'default' | 'advance' | 'none',
) {
  if (
    !section
    || !elementalType?.metadata.layout[section]
    || elementalType.metadata.layout[section] === 'none'
  )
    return false

  // If locationMatcher is provided, compare with the metadata
  if (locationMatcher)
    return elementalType.metadata.layout[section] === locationMatcher

  // If no locationMatcher is provided, just return true
  return true
}

export function getElementalType(
  data: Elemental,
  elementalTypeRoute?: string,
): ElementalType | undefined {
  if (elementalTypeRoute)
    return useElementalTypes().value.find(e => e.name === elementalTypeRoute)

  const elementalTypeId
    // eslint-disable-next-line no-mixed-operators
    = data.isList || !data.elementalTypeId ? 0 : data.elementalTypeId || 1
  return useElementalTypes().value.find(e => e.id === elementalTypeId)
}

export function getElementalDefinitions(
  type: ElementalTypeEnum,
): ElementalType | undefined {
  return useElementalTypes().value.find(e => e.id === type)
}

export type ElementalVisibility = 'ALL' | 'NONE' | 'PARTIAL'

export enum ElementalVisibilityEnum {
  ALL = -1,
  NONE = 0,
  PARTIAL = 3,
}

export function getVisibilityEnum(
  visibility: keyof typeof ElementalVisibilityEnum,
): ElementalVisibilityEnum {
  switch (visibility) {
    case 'ALL':
      return ElementalVisibilityEnum.ALL
    case 'PARTIAL':
      return ElementalVisibilityEnum.PARTIAL
    default:
      return ElementalVisibilityEnum.NONE
  }
}

export function getVisibilityEnumKey(
  visibility: ElementalVisibilityEnum,
): keyof typeof ElementalVisibilityEnum {
  switch (visibility) {
    case ElementalVisibilityEnum.ALL:
      return 'ALL'
    case ElementalVisibilityEnum.PARTIAL:
      return 'PARTIAL'
    default:
      return 'NONE'
  }
}

export function getVisibilityLabels(
  visibility: ElementalVisibilityEnum,
): string {
  switch (visibility) {
    case ElementalVisibilityEnum.ALL:
      return 'All'
    case ElementalVisibilityEnum.PARTIAL:
      return 'Partial'
    default:
      return 'Public'
  }
}

export enum ElementalTypeEnum {
  List = 0,
  Prompt = 1,
  Snippet = 2,
  Document = 3,
  Output = 4,
  Automation = 5,
  Course = 6,
  Table = 7,
  Cell = 8,
  Agent = 9,
  Tag = 10,
  Image = 11,
  unknown = 404,
}

export type ViewType = 'list' | 'grid'

interface FileSubcategory {
  id: number
  name: string
  color: string
  iconUrl: string
  origin: string
}

export interface Elemental {
  id: string
  title: string
  description: string
  template?: string
  url: string
  avatar?: string
  images: {
    output: PromptImage[]
    cover: PromptImage[]
  }
  user: User
  userId?: string
  nSaves?: number
  nVotes?: number
  nUses?: number
  nUserUses?: number
  nFiles?: number
  nPrompts?: number
  isSaved?: boolean
  isVoted?: boolean
  isPrivate?: boolean
  isPremium: boolean
  companiesSection?: boolean
  isPrompt?: boolean
  isList?: boolean
  topicIds?: number[]
  topicId?: number[]
  topics?: number[]
  tutorialStep?: TutorialStep[]
  elementalType?: string
  elementalTypeId: ElementalTypeEnum
  contentTypeId?: ElementalTypeEnum
  shareImage?: string
  price?: number
  priceOriginal?: number | null
  priceTypeId?: PriceType
  prompts?: Elemental[]
  discountPercentual?: number
  video?: string
  typeId?: number
  typeName?: string
  isTutorialHidden?: boolean
  recentItemAction?: RecentItemActionEnum
  isFeatured?: boolean
  createdAt: string
  updatedAt: string
  elementalsVisibility?: keyof typeof ElementalVisibilityEnum
  attachmentsVisibility?: keyof typeof ElementalVisibilityEnum
  attachments: File[]
  listIds?: string[]
  elemental_type_id?: ElementalTypeEnum
  itemIds?: string[]
  filesSubcategories?: FileSubcategory[]
  field?: string
  metadata?: Record<string, any> | string
  command?: any
  tableId?: string
  tableIndex?: number
  tableOrientation?: 'row' | 'column'
  rowId?: string
  colId?: string
}

export type ElementalFormData = Omit<
  Elemental,
  | 'elementalsVisibility'
  | 'attachmentsVisibility'
  | 'user'
  | 'id'
  | 'createdAt'
  | 'updatedAt'
> & {
  id?: string
  listId?: string[]
  listIds?: string[]
  isTutorialHidden?: boolean
  command?: string
  source?: string
  command_key?: string
  coverImages?: File[]
  outputImages?: File[]
  isFixedPrice?: boolean
  elementalsVisibility?:
  | ElementalVisibilityEnum
  | keyof typeof ElementalVisibilityEnum
  attachmentsVisibility?:
  | ElementalVisibilityEnum
  | keyof typeof ElementalVisibilityEnum
  contentTypeId?: ElementalTypeEnum
} & Record<string, any>

export type ElementalTypeRoute =
  | 'list'
  | 'prompt'
  | 'snippet'
  | 'document'
  | 'output'
  | 'automation'
  | 'course'
  | 'table'
  | 'cell'
  | 'unknown'
  | 'agent'
  | 'tag'
  | 'image'

export interface ElementalFile {
  id: number
  elementalId: string
  name: string
  url?: string
  createdAt: string
  size?: number
}

export const ElementalTypeRoutes = {
  0: 'list',
  1: 'prompt',
  2: 'snippet',
  3: 'document',
  4: 'output',
  5: 'automation',
  6: 'course',
  7: 'table',
  8: 'cell',
  404: 'unknown',
}

export interface ViewSettings {
  showUpvote: boolean
  defaultView: ViewType
  upvoteType?: 'upvote' | 'like'
  showDownload?: boolean
  showZoomIcon?: boolean
  showSearch?: boolean
  filters?: Record<string, any>
}

const ElementalSettings: Record<string, ViewSettings> = {
  TextPrompt: {
    showUpvote: true,
    upvoteType: 'upvote',
    showDownload: false,
    showZoomIcon: false,
    showSearch: true,
    defaultView: 'grid',
  },
  VisualPrompt: {
    showUpvote: true,
    upvoteType: 'like',
    showDownload: false,
    showZoomIcon: false,
    showSearch: true,
    defaultView: 'grid',
  },
  PromptList: {
    showUpvote: true,
    upvoteType: 'upvote',
    showDownload: false,
    showZoomIcon: false,
    showSearch: true,
    defaultView: 'grid',
  },
  Snippet: {
    showUpvote: false,
    upvoteType: 'upvote',
    showDownload: false,
    showZoomIcon: false,
    showSearch: true,
    defaultView: 'grid',
  },
  Image: {
    showUpvote: true,
    upvoteType: 'like',
    showDownload: true,
    showZoomIcon: true,
    showSearch: true,
    defaultView: 'grid',
  },
  Blog: {
    showUpvote: true,
    upvoteType: 'like',
    showDownload: false,
    showZoomIcon: false,
    showSearch: true,
    defaultView: 'grid',
  },
  Course: {
    showUpvote: true,
    upvoteType: 'like',
    showDownload: false,
    showZoomIcon: false,
    showSearch: true,
    defaultView: 'grid',
  },
}

export function getSetting(elementalType: string): ViewSettings {
  return ElementalSettings[elementalType]
}

/**
 * Temporary function to convert elemental to list
 * TODO: remove this as soon as we have a proper solution
 */
export function toList(elemental: Elemental): List {
  return {
    ...elemental,
    images: elemental.images?.output,
  } as unknown as List
}

export const elementalKeysToFields: Record<string, keyof ElementalFormData> = {
  title: 'title',
  description: 'description',
  saveToList: 'listIds',
  template: 'template',
  isPrivate: 'isPrivate',
  isPremium: 'isPremium',
  type: 'typeId',
  topic_ids: 'topicIds',
  command_slash: 'command',
  source: 'source',
  command_key: 'command_key',
  video_url: 'video',
  howto_section: 'tutorialStep',

  // --- Done apart (uploads):
  // avatar: 'avatar',
  // attachments: 'attachments',
  // images: 'images',
  // cover_images: 'cover_images',

  // --- Not implemented yet:
  // featured_review: 'advanced',
  // company_section: 'advanced',
  // features_section: 'advanced',
  // testimonial_section: 'advanced',
  // tweets_section: 'advanced',
  // addicitional_text_section: 'advanced',
}

export const INITIAL_ELEMENTAL: Partial<Elemental> = {
  title: 'Untitled',
  description: '',
  topicIds: [],
  listIds: [],
  isPrivate: true,
  isPremium: false,
  typeId: 1,
  isTutorialHidden: false,
  tutorialStep: [],
  video: '',
  priceTypeId: PriceType.fixed,
  priceOriginal: undefined,
  elementalsVisibility: 'NONE',
  template: '',
  attachments: [],
  metadata: {},
}

export interface OwnershipInformation {
  isBought: boolean
  nSales: number
  template?: string
  isTipable?: boolean
  tutorialStep?: TutorialStep[]
}
